import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Store, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { POST_LOGIN_KEY } from "@/components/AuthCallback";
import { destFor } from "@/lib/roles";

const HERO = "https://images.unsplash.com/photo-1742036626607-3ae1ac406cae?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const dest = from ? `${from.pathname}${from.search}` : "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={destFor(user, dest)} replace />;

  const googleLogin = () => {
    sessionStorage.setItem(POST_LOGIN_KEY, dest);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(destFor(u, dest), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-slate-900 lg:block">
        <img src={HERO} alt="Warung" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="grain absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Kasir untuk UMKM</p>
          <h1 className="mt-3 max-w-md font-display text-4xl font-extrabold leading-tight sm:text-5xl">
            Catat jualan, pantau stok, tanpa ribet.
          </h1>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="rise w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white">
              <Store className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-xl font-extrabold text-slate-900">KasirUMKM</p>
              <p className="text-sm text-slate-500">Masuk sebagai pemilik toko</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-11" />
          </div>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="h-11 w-full bg-emerald-600 hover:bg-emerald-700" data-testid="login-submit-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Masuk
          </Button>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> atau <span className="h-px flex-1 bg-slate-200" />
          </div>
          <Button type="button" variant="outline" onClick={googleLogin} className="h-11 w-full gap-2" data-testid="login-google-button">
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.7z" />
              <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z" />
              <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a12 12 0 0 0 0 10.8l4-3.1z" />
              <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z" />
            </svg>
            Masuk dengan Google
          </Button>
          <p className="text-center text-xs text-slate-400">Login Google hanya untuk email yang sudah didaftarkan pemilik toko.</p>
        </form>
      </div>
    </div>
  );
}
